import React, { createContext, useContext, useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { sendDataAPI } from '@lib/sendDataAPI';
import { bigCommerceSDK } from '@/scripts/bcSdk';

interface SessionContextProps {
	children: React.ReactNode;
	initialContext: string;
	onLoad?: () => void;
	onError?: (error: string) => void;
}

const SessionContext = createContext({
	context: '',
	user: {
		frontUrl: '',
		createdAt: '',
		planName: '',
		isFree: true,
		isNotConfigured: true,
		timezoneName: '',
	},
	isLoading: true,
});

export interface InstanceDataResponse extends Response {
	data?: {
		appId: string;
		instanceId: string;
		frontUrl: string;
		createdAt: string;
		planName: string;
		isFree: boolean;
		isNotConfigured: boolean;
	};
	message?: string;
}

const SessionProvider: React.FC<SessionContextProps> = ({ children, initialContext, onLoad, onError }) => {
	const { query } = useRouter();

	const [context, setContext] = useState(initialContext || '');
	const [isLoading, setIsLoading] = useState(true);
	const [user, setUser] = useState({
		frontUrl: '',
		createdAt: '',
		planName: '',
		isFree: true,
		isNotConfigured: true,
		timezoneName: '',
	});

	useEffect(() => {
		if (query.context) {
			setContext(query.context as string);
		}
	}, [query.context]);

	useEffect(() => {
		const fetchAccess = async () => {
			try {
				const rawResponse = await sendDataAPI({ jwtToken: context }, '/api/instance/get_info', 'GET');
				const jsonData = await rawResponse.json();

				if (jsonData.data) {
					setUser({
						frontUrl: jsonData.data.frontUrl || '',
						createdAt: jsonData.data.createdAt || '',
						planName: jsonData.data.planName || '',
						isNotConfigured: jsonData.data.isNotConfigured,
						isFree: jsonData.data.isFree,
						timezoneName: jsonData.data.timezoneName,
					});
				} else {
					throw new Error('No data received from API');
				}
			} catch (err) {
				console.error(err);
				if (onError) {
					onError('Something went wrong... Please, try again');
				}
			} finally {
				setIsLoading(false);
				if (onLoad) {
					onLoad();
				}
			}
		};

		if (context) {
			fetchAccess();
			// Keeps app in sync with BC (e.g. heartbeat, user logout, etc.)
			bigCommerceSDK(context);
		}
	}, [context, onLoad, onError]);

	if (isLoading) {
		return null;
	}

	return <SessionContext.Provider value={{ context, user, isLoading }}>{children}</SessionContext.Provider>;
};

export const useSession = () => useContext(SessionContext);

export default SessionProvider;
